import { useEffect, useRef } from 'react';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { useTeamChatSelector } from '../team-chat.provider';

const useUpdateUnreadMentionsCount = () => {
  const messagesWithMentions = useTeamChatSelector((ctx) => ctx.messagesWithMentions);
  const conversations = useTeamChatSelector((ctx) => ctx.conversations);
  const mentionsUnreadCount = useTeamChatSelector((ctx) => ctx.unreadMentionsCount);
  const setUnreadMentionsCount = useTeamChatSelector((ctx) => ctx.cache.setUnreadMentionsCount);
  const setMentions = useTeamChatSelector((ctx) => ctx.setMentions);
  const isOpen = useTeamChatSelector((ctx) => ctx.isOpen);
  const isFetchingMentions = useTeamChatSelector((ctx) => ctx.isFetchingMentions);
  const shouldSetMentions = useRef(isOpen);

  // when mentions are fetched it does not have an updated message count because message status is not available
  // Hence unread mentions count function is not reliable. With this hook we are going to map the mentioned
  // messages with the messages in the channel and get their read status.

  useEffect(() => {
    // We do not update the state when do not have mentions or when we are fetching the latest mentions
    if (!messagesWithMentions || isFetchingMentions) return;
    const clubbedConversation: TeamChatTypes.Conversation[] = [
      ...(conversations?.dm ?? []),
      ...(conversations?.groups ?? []),
    ];
    if (!clubbedConversation || clubbedConversation.length === 0) return;
    const conversationMap = clubbedConversation.reduce<Record<string, number>>((acc, { channelId }, index) => {
      acc[channelId] = index;
      return acc;
    }, {});
    // Pick individual channels and compare it's last read messages for this user to mark each message as read/unread
    const updatedMessagesWithMentions = messagesWithMentions.map((message) => {
      const conversationIndex = conversationMap[message.channelId];
      const conversation = clubbedConversation[conversationIndex];
      // Even lastMessageReadAt is required, it comes as undefined. Patching it so that it does not throws error
      // and @MehulWeave will pick this
      if (conversation?.lastMessageReadAt) {
        // Check if the message was created after the last read time (making it unread)
        const lastReadTime = new Date(conversation.lastMessageReadAt);
        const messageTime = new Date(message.createdAt);
        const isUnread = messageTime > lastReadTime;
        return { ...message, isUnread };
      }
      return message;
    });
    const updatedUnreadCount = updatedMessagesWithMentions.filter((message) => message.isUnread).length;
    // Only update the mentions if the unread count comes to be different or the team chat panel is opened.
    if (mentionsUnreadCount !== updatedUnreadCount || shouldSetMentions.current) {
      shouldSetMentions.current = false;
      setUnreadMentionsCount(updatedUnreadCount);
      setMentions(updatedMessagesWithMentions);
    }
  }, [messagesWithMentions, conversations, mentionsUnreadCount, isFetchingMentions]);

  useEffect(() => {
    if (isOpen) {
      shouldSetMentions.current = true;
    }
  }, [isOpen]);
};

export const _useUpdateUnreadMentionsCount = useUpdateUnreadMentionsCount;
